import { EXTEND, NO, UIView, YES } from "uicore-ts"
import { RHContractDuration, RHProcurementPart } from "../../../models/ProcurementModels"
import { RHCore } from "../Core/RHCore"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton, type RHButtonVariant } from "../Custom components/RH/RHButton"
import type { RHValidationIssue } from "../Custom components/RH/RHValidationIssuesDialogView"
import { RHButtons, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurerMainViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import {
    InvitedCompany,
    RHCPVSummationInputPart,
    RHCPVSummationReport,
    RHCPVSummationReportRequest,
    RHProcurementTemplateInvitationImportResult,
    RHProcurementTemplateInvitationImportRow,
    WithNullsForOptional
} from "../SocketClientFunctions"
import type { CPVSummationRowInfo } from "./CPVTreeDialogView"
import { ProcurementPartEditorView } from "./ProcurementPartEditorView"
import { ProcurementInvitationState } from "./ProcurementInvitationState"
import type { ProcurementTemplateLockControlConfiguration } from "./ProcurementTemplateLockButton"

type EditableProcurementPart = Partial<RHProcurementPart> & {
    sourceTemplatePartId?: string
}

type TemplatePartProtection = {
    description?: boolean
    estimatedCost?: boolean
    isDescriptionHidden?: boolean
    isEstimatedCostHidden?: boolean
    isCpvHidden?: boolean
    isPartsHidden?: boolean
    isContractDurationHidden?: boolean
    isInvitedCompaniesHidden?: boolean
    cpv: boolean
    parts?: boolean
    contractDuration?: boolean
    requiredDocuments: boolean
    invitedCompanies: boolean
    canOverrideLockedSupplierSourceCPVs: boolean
    persistsCompanyCustomization: boolean
    invitationImportResolver?: (
        rows: RHProcurementTemplateInvitationImportRow[]
    ) => Promise<RHProcurementTemplateInvitationImportResult>
}

type TemplatePartAuthoringLocks = {
    description?: ProcurementTemplateLockControlConfiguration
    estimatedCost?: ProcurementTemplateLockControlConfiguration
    contractDuration?: ProcurementTemplateLockControlConfiguration
    cpv: ProcurementTemplateLockControlConfiguration
    invitedCompanies: ProcurementTemplateLockControlConfiguration
}


/**
 * Self-contained editor that manages an ordered list of ProcurementPartEditorView
 * cards. The controller sets `procurementParts` once to populate (or clear) the
 * editors, and reads it back when saving. All internal add/remove orchestration,
 * part-number renumbering, and canRemove toggling happen here.
 *
 * The controller subscribes to `partsDidChange` to react to cost-summary or
 * duration-section changes without knowing anything about the internal view array.
 */
export class ProcurementPartsEditorView extends UIView {

    // ─── Internal state ───────────────────────────────────────────────────────

    _partEditorViews: ProcurementPartEditorView[] = []
    _invitedCompaniesButtonVariant: RHButtonVariant = RHButtons.compactPrimaryVariant
    _templateProtection?: TemplatePartProtection
    _templateAuthoringLocks?: TemplatePartAuthoringLocks
    _readOnlyPartIds: string[] = []

    get readOnlyPartIds(): string[] {
        return this._readOnlyPartIds
    }

    set readOnlyPartIds(readOnlyPartIds: string[]) {
        this._readOnlyPartIds = [...readOnlyPartIds]
        for (const editor of this._partEditorViews) {
            this._applyTemplateProtectionToEditor(editor)
        }
        this._refreshEditorStates()
        this._updateCopyDurationButtonVisibility()
        this.setNeedsLayoutUpToRootView()
    }

    // ─── Callbacks ────────────────────────────────────────────────────────────

    /**
     * Called whenever the set of parts changes (add, remove) or any cost / CPV
     * field inside a part changes. The controller uses this to refresh the cost
     * summary and the duration section.
     */
    partsDidChange?: () => void
    cpvTreeSelectionDidChange?: () => void
    cpvSummationInfoForEditorCPVCode?: (
        editor: ProcurementPartEditorView,
        cpvCode: string,
        shouldLoad?: boolean
    ) => CPVSummationRowInfo | undefined

    // ─── Subviews ─────────────────────────────────────────────────────────────

    partsContainerView = new UIView().addedAsSubviewToView(this).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            this.partsContainerView
                .contentBoundsWithInset(0)
                .framesByDistributingViewsAsColumn(
                    this._partEditorViews,
                    RHSpacing.medium
                )
        })
    })

    addPartButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        titleLabel: { text: "+ Lisa osa" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                this._addPart(YES)
            }
        }
    })

    // ─── Focus management ─────────────────────────────────────────────────────

    /**
     * Nested focus manager — include this in the parent controller's
     * `_focusManager` items list. It is rebuilt on every Tab keypress from the
     * current live editor array, so newly added / removed parts are always
     * reflected without any extra wiring.
     */
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        this._partEditorViews.forEach(editor => {
            if (editor.canRemove) {
                items.push(editor.removePartButton)
            }
            items.push(editor.descriptionTemplateLockButton, editor.descriptionTemplateLockButton.visibilityButton)
            items.push(editor.descriptionEditor)
            items.push(editor.estimatedCostTemplateLockButton, editor.estimatedCostTemplateLockButton.visibilityButton)
            items.push(editor.estimatedCostTextField)
            if (editor.cpvTemplateLockButton.superview === editor) {
                items.push(editor.cpvTemplateLockButton, editor.cpvTemplateLockButton.visibilityButton)
            }
            items.push(editor.cpvCodeTextField)
            if (editor.cpvSummationPartReportView.superview === editor) {
                items.push(...editor.cpvSummationPartReportView.focusableViews)
            }
            items.push(editor.openCPVTreeButton)
            items.push(editor.openInvitedCompaniesButton)
            if (editor.invitedCompaniesTemplateLockButton.superview === editor) {
                items.push(editor.invitedCompaniesTemplateLockButton, editor.invitedCompaniesTemplateLockButton.visibilityButton)
            }
            if (editor.copyDurationFromAboveButton.superview === editor) {
                items.push(editor.copyDurationFromAboveButton)
            }
            if (editor.contractDurationTemplateLockButton.superview === editor) {
                items.push(editor.contractDurationTemplateLockButton, editor.contractDurationTemplateLockButton.visibilityButton)
            }
            if (editor.durationSpecifiedInDocsButton.superview === editor) {
                items.push(editor.durationSpecifiedInDocsButton)
                items.push(editor.durationInMonthsButton)
                items.push(editor.durationInDaysButton)
                items.push(editor.durationAsDateRangeButton)
                if (editor.contractDurationMonthsTextField.superview === editor) {
                    items.push(editor.contractDurationMonthsTextField)
                }
                if (editor.contractDurationDaysTextField.superview === editor) {
                    items.push(editor.contractDurationDaysTextField)
                }
                if (editor.contractDurationStartDatePicker.superview === editor) {
                    items.push(editor.contractDurationStartDatePicker)
                    items.push(editor.contractDurationEndDatePicker)
                }
            }
        })
        return items
    })

    // ─── Data property ────────────────────────────────────────────────────────

    /**
     * Setting this replaces all current editors with new ones populated from
     * the supplied array. An empty array resets to a single blank part.
     * Reading this returns the current editor values as plain part objects.
     */
    get procurementParts(): Array<WithNullsForOptional<EditableProcurementPart>> {
        return this._partEditorViews.map((editor, index) => {
            if (editor.isReadOnly && editor.sourcePart) {
                return { ...editor.sourcePart, _id: editor.partIdentifier }
            }
            let description: string | null = null
            let descriptionFormat: "plaintext" | "html" | null = null
            let estimatedCost: number | null = null
            const estimatedCostValue = editor.estimatedCostTextField.numericValue
            if (!editor.descriptionEditor.isEmpty) {
                description = editor.descriptionEditor.html
                descriptionFormat = "html"
            }
            if (Number.isFinite(estimatedCostValue)) {
                estimatedCost = estimatedCostValue
            }
            // Quill normalizes loaded HTML, so locked descriptions retain their stored representation.
            if (this._templateProtection?.description) {
                description = editor.sourcePart?.description ?? null
                descriptionFormat = editor.sourcePart?.descriptionFormat ?? null
            }
            if (this._templateProtection?.estimatedCost) {
                estimatedCost = editor.sourcePart?.estimatedCost ?? null
            }
            const procurementPart: WithNullsForOptional<EditableProcurementPart> = {
                _id: editor.partIdentifier,
                partNumber: index + 1,
                description,
                descriptionFormat,
                CPV: editor.cpvCodeTextField.text.split(" ").firstElement || null,
                estimatedCost,
                contractDuration: editor.contractDuration ?? null
            }
            if (editor.sourceTemplatePartId) {
                procurementPart.sourceTemplatePartId = editor.sourceTemplatePartId
            }
            if (editor.additionalInviteSourceCPVCodes.length > 0) {
                procurementPart.additionalInviteSourceCPVCodes = [
                    ...editor.additionalInviteSourceCPVCodes
                ]
            }
            return procurementPart
        })
    }

    set procurementParts(parts: EditableProcurementPart[]) {
        this._removeAllEditors()

        const partsToCreate = parts.length > 0 ? parts : [{}]

        partsToCreate.forEach((part, index) => {
            const editor = this._makeEditor(
                index + 1,
                ProcurementPartsEditorView._partIdentifierFromValue(part._id)
            )
            editor.sourcePart = { ...part }
            editor.sourceTemplatePartId = part.sourceTemplatePartId
            this._applyTemplateProtectionToEditor(editor)
            editor.addedAsSubviewToView(this.partsContainerView)

            if (part.description) {
                if (part.descriptionFormat === "html") {
                    editor.descriptionEditor.html = part.description
                }
                else {
                    editor.descriptionEditor.loadPlainText(part.description)
                }
            }
            if (part.CPV) {
                editor.setCPVCodeFromSavedValue(part.CPV)
            }
            editor.restoreAdditionalInviteSourceCPVCodes(
                part.additionalInviteSourceCPVCodes ?? []
            )
            if (typeof part.estimatedCost === "number") {
                editor.estimatedCostTextField.text = part.estimatedCost.toString()
                editor.estimatedCostTextField._showFormattedDisplay()
            }

            if (part.contractDuration) {
                editor.contractDuration = part.contractDuration
            }

            this._partEditorViews.push(editor)
        })

        this._refreshEditorStates()
        this._updateCopyDurationButtonVisibility()
        this.setNeedsLayoutUpToRootView()
    }


    get invitedCompaniesButtonVariant(): RHButtonVariant {
        return this._invitedCompaniesButtonVariant
    }

    set invitedCompaniesButtonVariant(variant: RHButtonVariant) {
        this._invitedCompaniesButtonVariant = variant
        for (const editor of this._partEditorViews) {
            editor.openInvitedCompaniesButton.variant = variant
        }
    }


    configureTemplateProtection(protection?: TemplatePartProtection) {
        this._templateProtection = protection
        for (const editor of this._partEditorViews) {
            this._applyTemplateProtectionToEditor(editor)
        }
        this._refreshEditorStates()
        this._updateCopyDurationButtonVisibility()
        this.setNeedsLayoutUpToRootView()
    }


    configureTemplateAuthoringLocks(locks?: TemplatePartAuthoringLocks) {
        this._templateAuthoringLocks = locks
        for (const editor of this._partEditorViews) {
            this._applyTemplateAuthoringLocksToEditor(editor)
        }
        this.setNeedsLayoutUpToRootView()
    }

    // ─── Computed helpers ─────────────────────────────────────────────────────

    /**
     * Returns a lightweight descriptor for each current part — enough for
     * `RequiredDocumentsEditorView.parts`. The controller passes this straight
     * through without knowing about the internal editor array.
     */
    get partIdentifiers(): { partNumber: number; _id: string }[] {
        return this._partEditorViews.map((editor, index) => ({
            partNumber: index + 1,
            _id: editor.partIdentifier
        }))
    }

    get partCount(): number {
        return this._partEditorViews.length
    }

    /**
     * CPV codes currently entered across all parts (trimmed, non-empty).
     * Used by the controller's duration-section applicability check.
     */
    get cpvCodes(): string[] {
        return this._partEditorViews
            .map(editor => editor.cpvCodeTextField.text.split(" ").firstElement?.trim() ?? "")
            .filter(code => code.length > 0)
    }


    /**
     * Canonical selected-company snapshot across the current live part editors.
     * Each editor contributes only invited rows and stamps its current CPV code,
     * so deleted or changed parts cannot leave stale associations behind.
     */
    get invitedCompanies(): InvitedCompany[] {
        return ProcurementInvitationState.selectedCompaniesForParts(
            this._partEditorViews.map(editor => ({
                partId: editor.partIdentifier,
                cpvCode: editor.cpvCodeTextField.text.split(" ").firstElement?.trim() ?? "",
                companies: editor.invitedCompanies
            }))
        )
    }


    /**
     * Cost entries for the CostSummaryView — one entry per part.
     */
    get costEntries(): { cpvCode: string; cost: number }[] {
        return this._partEditorViews.map(editor => {
            let cost = editor.estimatedCostTextField.numericValue
            if (!Number.isFinite(cost)) {
                cost = 0
            }
            return {
                cpvCode: editor.cpvCodeTextField.text.split(" ").firstElement ?? "",
                cost
            }
        })
    }


    async prepareForValidation() {
        await Promise.all(
            this._partEditorViews.map(editor => editor.prepareCPVCodeForValidation())
        )
    }


    /**
     * Restores the invited-companies state from a previously saved procurement's
     * `invitedCompanies` array. Must be called *after* `procurementParts` is set
     * and `prepareForValidation` has resolved (so CPV codes are normalised and
     * `_cpvCodeForLastLoadedCompanyCounts` has been set by the initial auto-fetch).
     *
     * A single part receives the complete snapshot. With multiple parts, each
     * editor receives the subset associated with its stable part ID; snapshots
     * created before part IDs use normalized CPV association as a fallback.
     */
    restoreInvitedCompanies(allSavedCompanies: InvitedCompany[]) {
        for (const editor of this._partEditorViews) {
            const cpvCode = editor.cpvCodeTextField.text.split(" ").firstElement?.trim() ?? ""
            const companiesForPart = ProcurementInvitationState.companiesForRestoredPart(
                allSavedCompanies,
                this._partEditorViews.length,
                editor.partIdentifier,
                cpvCode
            )

            editor.restoreInvitedCompaniesFromSavedList(companiesForPart)
        }
    }


    updateCPVSummationReport(
        report: RHCPVSummationReport | undefined,
        request?: RHCPVSummationReportRequest
    ) {
        const partsByNumber = new Map<number, RHCPVSummationInputPart>()
        if (request) {
            for (const part of request.parts) {
                if (typeof part.partNumber === "number") {
                    partsByNumber.set(part.partNumber, part)
                }
            }
        }

        this._partEditorViews.forEach((editor, index) => {
            const partNumber = index + 1
            let part = partsByNumber.get(partNumber)
            if (!part && request && request.parts.length === 1 && this._partEditorViews.length === 1) {
                part = request.parts[0]
            }
            editor.updateCPVSummationReport(report, part)
        })

        this.partsContainerView.setNeedsLayout()
        this.setNeedsLayoutUpToRootView()
    }

    // ─── Validation ───────────────────────────────────────────────────────────

    /** Validates editable parts in visual order and returns every user-facing reason. */
    validateFields(): RHValidationIssue[] {
        const validationIssues: RHValidationIssue[] = []

        this._partEditorViews.forEach(editor => {
            if (editor.isReadOnly) {
                return
            }
            editor.normalizeCPVCodeText()
            if (!editor.validateDescription()) {
                validationIssues.push({
                    focusTarget: editor.descriptionEditor,
                    message: `Hankeosal ${editor.partNumber} on kirjeldus puudu.`
                })
            }
            if (!editor.estimatedCostTextField.validate()) {
                validationIssues.push({
                    focusTarget: editor.estimatedCostTextField,
                    message: `Hankeosa ${editor.partNumber} väljal „Eeldatav maksumus (KM-ta)“ peab olema nullist suurem või sellega võrdne arv.`
                })
            }
            if (!editor.validateCPVCode()) {
                let message = `Hankeosal ${editor.partNumber} peab olema valitud CPV-kood.`
                if (editor.cpvCodeTextField.text.trim().length > 0) {
                    message = `Hankeosa ${editor.partNumber} väljal „CPV-kood“ peab olema valitud väärtus CPV-loendist.`
                }
                validationIssues.push({
                    focusTarget: editor.cpvCodeTextField,
                    message
                })
            }
            if ((!this._templateAuthoringLocks || this._templateAuthoringLocks.contractDuration?.locked) &&
                !editor.validateDuration()) {
                validationIssues.push({
                    focusTarget: editor.firstInvalidDurationField() ?? editor.durationSpecifiedInDocsButton,
                    message: editor.contractDurationValidationMessage() ??
                        `Hanke osal ${editor.partNumber} on lepingu kestus vigane.`
                })
            }
        })
        return validationIssues
    }

    // ─── Internal helpers ─────────────────────────────────────────────────────

    _makeEditor(
        partNumber: number,
        partIdentifier: string = ProcurementPartsEditorView._newPartIdentifier()
    ): ProcurementPartEditorView {
        const editor: ProcurementPartEditorView =
            new ProcurementPartEditorView().configuredWithObject({
            partNumber,
            partIdentifier,
            openInvitedCompaniesButton: { variant: this.invitedCompaniesButtonVariant },
            removalWasRequested: () => this._removePart(editor)
        })
        editor.summationInfoForCPVCode = (cpvCode, shouldLoad) =>
            this.cpvSummationInfoForEditorCPVCode?.(editor, cpvCode, shouldLoad)
        editor.cpvTreeSelectionDidChange = () => this.cpvTreeSelectionDidChange?.()
        this._applyTemplateProtectionToEditor(editor)
        this._applyTemplateAuthoringLocksToEditor(editor)
        editor.descriptionEditor.editorDidChange = () => {
            if (editor.descriptionValidationMessageLabel.superview === editor) {
                editor.validateDescription()
            }
            this.partsDidChange?.()
        }
        editor.partDidChange = () => {
            this._updateCopyDurationButtonVisibility()
            this.partsDidChange?.()
        }
        editor.estimatedCostTextField.controlEventTargetAccumulator.TextChange = () => {
            this.partsDidChange?.()
        }
        editor.cpvCodeTextField.controlEventTargetAccumulator.TextChange = (sender, event) => {
            // Blur repeats the already handled autocomplete selection as a native change event.
            if (event?.type === "change") {
                return
            }
            if (editor.cpvCodeValidationMessageLabel.superview === editor) {
                editor.validateCPVCode()
            }
            editor.clearCPVSummationReport()
            editor.partDidChange?.()
        }
        editor.cpvCodeTextField.controlEventTargetAccumulator.SelectionDidChange = (sender, event) => {
            if (editor.cpvCodeValidationMessageLabel.superview === editor) {
                editor.validateCPVCode()
            }
            editor.clearCPVSummationReport()
            editor.partDidChange?.()
        }
        // noinspection JSConstantReassignment
        editor.copyDurationFromAboveButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._copyDurationFromPrecedingPart(editor)
        }
        
        return editor
    }


    static _partIdentifierFromValue(value: string | number | object | null | undefined): string {
        const identifier = (value ?? "").toString()
        if (RHCore.isCandidateIdAValidObjectId(identifier)) {
            return identifier
        }
        return ProcurementPartsEditorView._newPartIdentifier()
    }


    static _newPartIdentifier(): string {
        const bytes = crypto.getRandomValues(new Uint8Array(12))
        return Array.from(bytes)
            .map(byte => byte.toString(16).padStart(2, "0"))
            .join("")
    }

    private _addPart(focusNewPart: boolean = NO) {
        if (this._templateProtection?.parts || this._templateProtection?.contractDuration ||
            this._templateProtection?.description || this._templateProtection?.estimatedCost) {
            CBDialogViewShower.alert("Hankeosade lisamine ja eemaldamine on hankemalli lukustuste tõttu piiratud.")
            return
        }
        if (this._templateProtection?.cpv) {
            CBDialogViewShower.alert("CPV-koodid ja hankeosade struktuur on hankemallis lukustatud.")
            return
        }
        const partNumber = this._partEditorViews.length + 1
        const editor = this._makeEditor(partNumber)
        editor.addedAsSubviewToView(this.partsContainerView)
        this._partEditorViews.push(editor)

        this._refreshEditorStates()
        this._updateCopyDurationButtonVisibility()
        this.partsDidChange?.()
        this.setNeedsLayoutUpToRootView()

        if (focusNewPart) {
            editor.descriptionEditor.focus()
        }
    }

    private _removePart(editor: ProcurementPartEditorView) {
        const editorIndex = this._partEditorViews.indexOf(editor)
        if (this._partEditorViews.slice(editorIndex).some(partEditor => partEditor.isReadOnly)) {
            return
        }
        if (this._partEditorViews.length <= 1) {
            CBDialogViewShower.alert("Hange peab sisaldama vähemalt ühte osa")
            return
        }

        if (this._templateProtection?.parts || this._templateProtection?.contractDuration ||
            this._templateProtection?.description || this._templateProtection?.estimatedCost) {
            CBDialogViewShower.alert("Hankeosade lisamine ja eemaldamine on hankemalli lukustuste tõttu piiratud.")
            return
        }
        if (editor.sourceTemplatePartId && this._templateProtection?.cpv) {
            CBDialogViewShower.alert("CPV-koodid ja hankeosade struktuur on hankemallis lukustatud.")
            return
        }
        if (editor.sourceTemplatePartId && this._templateProtection?.requiredDocuments) {
            CBDialogViewShower.alert("Osa ei saa eemaldada, sest nõutud dokumendid on hankemallis lukustatud.")
            return
        }
        if (
            editor.sourceTemplatePartId &&
            this._templateProtection?.invitedCompanies &&
            (editor.invitedCompanies.some(company => company.isInvited) ||
                editor.additionalInviteSourceCPVCodes.length > 0) &&
            !this._templateProtection.canOverrideLockedSupplierSourceCPVs
        ) {
            CBDialogViewShower.alert("Osa ei saa eemaldada, sest pakkujate ring on hankemallis lukustatud.")
            return
        }

        const index = this._partEditorViews.indexOf(editor)
        if (index === -1) {
            return
        }

        this._partEditorViews.splice(index, 1)
        editor.removeFromSuperview()

        this._refreshEditorStates()
        this._updateCopyDurationButtonVisibility()
        this.partsDidChange?.()
        this.setNeedsLayoutUpToRootView()
    }


    /**
     * Finds the closest preceding part (by index) that has a non-null duration,
     * and returns it. Returns null when no such part exists before `editor`.
     */
    private _precedingDurationForEditor(editor: ProcurementPartEditorView): RHContractDuration | null {
        const index = this._partEditorViews.indexOf(editor)
        for (let precedingIndex = index - 1; precedingIndex >= 0; precedingIndex--) {
            const precedingDuration = this._partEditorViews[precedingIndex].contractDuration
            if (precedingDuration) {
                return precedingDuration
            }
        }
        return null
    }


    /**
     * Copies the duration from the closest preceding part into `editor`.
     * Called both when the copy button is pressed and automatically when
     * a new part is added (if a preceding duration exists).
     */
    private _copyDurationFromPrecedingPart(editor: ProcurementPartEditorView) {
        if (editor.isReadOnly || this._templateProtection?.contractDuration) {
            return
        }
        const duration = this._precedingDurationForEditor(editor)
        if (!duration) {
            return
        }
        editor.contractDuration = duration
        this.partsDidChange?.()
        this.setNeedsLayoutUpToRootView()
    }


    /** Shows the copy-duration action after the first part. */
    private _updateCopyDurationButtonVisibility() {
        this._partEditorViews.forEach((editor, index) => {
            if (index === 0 || editor.isReadOnly || this._templateProtection?.isContractDurationHidden) {
                editor.copyDurationFromAboveButton.removeFromSuperview()
                return
            }

            const hasPrecedingDuration = this._precedingDurationForEditor(editor) !== null

            if (hasPrecedingDuration) {
                editor.copyDurationFromAboveButton.addedAsSubviewToView(editor)
            }
            else {
                editor.copyDurationFromAboveButton.removeFromSuperview()
            }
        })
    }

    private _removeAllEditors() {
        this._partEditorViews.forEach(editor => editor.removeFromSuperview())
        this._partEditorViews = []
    }

    /**
     * Renumbers all editors and toggles the remove button based on current count.
     */
    private _refreshEditorStates() {
        const canRemove = this._partEditorViews.length > 1
        const partCount = this._partEditorViews.length
        this._partEditorViews.forEach((editor, index) => {
            const topologyAllowsRemoval = !(
                editor.sourceTemplatePartId &&
                (this._templateProtection?.cpv || this._templateProtection?.requiredDocuments)
            )
            editor.configureWithObject({
                partNumber: index + 1,
                partCount,
                canRemove: canRemove && topologyAllowsRemoval &&
                    !this._partEditorViews.slice(index).some(partEditor => partEditor.isReadOnly) &&
                    !this._templateProtection?.parts && !this._templateProtection?.contractDuration &&
                    !this._templateProtection?.description && !this._templateProtection?.estimatedCost
            })
        })
        this.addPartButton.enabled = !this._templateProtection?.cpv &&
            !this._templateProtection?.parts && !this._templateProtection?.contractDuration &&
            !this._templateProtection?.description && !this._templateProtection?.estimatedCost
        if (this._templateProtection?.isPartsHidden) {
            this.addPartButton.removeFromSuperview()
        }
        else {
            this.addPartButton.addedAsSubviewToView(this)
        }
        this.partsContainerView.setNeedsLayout()
    }


    _applyTemplateProtectionToEditor(editor: ProcurementPartEditorView) {
        editor.isReadOnly = this._readOnlyPartIds.includes(editor.partIdentifier)
        editor.configureTemplateProtection({
            isDescriptionLocked: !!this._templateProtection?.description,
            isEstimatedCostLocked: !!this._templateProtection?.estimatedCost,
            isDescriptionHidden: !!this._templateProtection?.isDescriptionHidden,
            isEstimatedCostHidden: !!this._templateProtection?.isEstimatedCostHidden,
            isCpvHidden: !!this._templateProtection?.isCpvHidden && !!editor.sourceTemplatePartId,
            isContractDurationHidden: !!this._templateProtection?.isContractDurationHidden,
            isInvitedCompaniesHidden: !!this._templateProtection?.isInvitedCompaniesHidden && !!editor.sourceTemplatePartId,
            isCpvLocked: !!this._templateProtection?.cpv && !!editor.sourceTemplatePartId,
            isContractDurationLocked: !!this._templateProtection?.contractDuration,
            isInvitedCompaniesLocked:
                !!this._templateProtection?.invitedCompanies && !!editor.sourceTemplatePartId,
            canOverrideLockedSupplierSourceCPVs:
                !!this._templateProtection?.canOverrideLockedSupplierSourceCPVs,
            persistsCompanyCustomization:
                this._templateProtection?.persistsCompanyCustomization ?? YES
        })
        editor.templateImportResolver = this._templateProtection?.invitationImportResolver
    }


    _applyTemplateAuthoringLocksToEditor(editor: ProcurementPartEditorView) {
        editor.configureTemplateAuthoringLocks(this._templateAuthoringLocks)
    }

    // ─── Layout ───────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)

        bounds
            .rectangleWithHeight(this.partsContainerView)
            .assignedAsFrameOfView(this.partsContainerView)

            .IF(this.addPartButton.superview === this)
            .rectangleForNextRow(
                RHSpacing.small,
                (RHButtons.compactHeight + 2)
            )
            .performingFunctionWithSelf(rowFrame => {
                let buttonWidth = RHProcurerMainViewTokens.actionButtonWidth
                const firstEditor = this._partEditorViews.firstElement
                if (firstEditor) {
                    this.partsContainerView.layoutIfNeeded()
                    if (firstEditor.openCPVTreeButton.frame.width > 0) {
                        buttonWidth = firstEditor.openCPVTreeButton.frame.width
                    }
                }
                rowFrame
                    .rectangleWithInsets(RHSpacing.medium, RHSpacing.medium, 0, 0)
                    .rectangleWithWidth(buttonWidth)
                    .assignedAsFrameOfView(this.addPartButton)
            })
            .ENDIF()
    }

}
